import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import apiClient from "@/api/client";
import { Button } from "@/components/ui/Button";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { useCreateBillingOrder, type CreatedBillingOrder } from "../api/clusters";
import type { DocumentationCluster } from "../types";

interface Agreement {
  id: string;
  title: string;
  contractor_name: string;
}

interface LineItemType {
  id: string;
  name: string;
  unit: string;
  base_rate: string;
}

interface Props {
  clusters: DocumentationCluster[];
  selectedClusterIds: string[];
  onClose: () => void;
  onCreated: (orders: CreatedBillingOrder[]) => void;
}

export function BillingOrderDialog({ clusters, selectedClusterIds, onClose, onCreated }: Props) {
  const [agreementId, setAgreementId] = useState("");
  const [lineItemTypeId, setLineItemTypeId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [createdOrders, setCreatedOrders] = useState<CreatedBillingOrder[] | null>(null);

  const selectedClusters = clusters.filter((c) => selectedClusterIds.includes(c.id));
  const contractorIds = [...new Set(selectedClusters.map((c) => c.contractor))];
  const singleContractorId = contractorIds.length === 1 ? contractorIds[0] : undefined;

  // Clusters may span several projects; one billing order is created per project.
  const projectGroups = Array.from(
    selectedClusters
      .reduce((map, c) => {
        const group = map.get(c.project) ?? { name: c.project_name, clusters: [] };
        group.clusters.push(c);
        map.set(c.project, group);
        return map;
      }, new Map<string, { name: string; clusters: DocumentationCluster[] }>())
      .values()
  );
  const multiProject = projectGroups.length > 1;

  const { data: agreements = [] } = useQuery({
    queryKey: ["agreements-for-billing", singleContractorId],
    queryFn: () =>
      apiClient
        .get<{ data: { results?: Agreement[]; } | Agreement[] }>("/agreements/", {
          params: singleContractorId ? { contractor: singleContractorId } : {},
        })
        .then((r) => {
          const d = r.data.data;
          return (d as { results?: Agreement[] }).results ?? (d as Agreement[]);
        }),
  });

  const { data: lineItemTypes = [] } = useQuery({
    queryKey: ["agreement-line-items", agreementId],
    queryFn: () =>
      apiClient
        .get<{ data: LineItemType[] }>(`/agreements/${agreementId}/line-items/`)
        .then((r) => r.data.data),
    enabled: !!agreementId,
  });

  const createMutation = useCreateBillingOrder();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!agreementId) { setError("Wybierz umowę ramową."); return; }
    if (!lineItemTypeId) { setError("Wybierz pozycję realizacyjną."); return; }
    if (selectedClusterIds.length === 0) { setError("Zaznacz przynajmniej jeden klaster."); return; }

    try {
      const orders = await createMutation.mutateAsync({
        framework_agreement_id: agreementId,
        line_item_type_id: lineItemTypeId,
        cluster_ids: selectedClusterIds,
      });
      onCreated(orders);
      setCreatedOrders(orders);
    } catch (err) {
      // Surface the backend's specific reason (wrong contractor, already settled, …)
      // instead of a fixed guess — a generic message previously masked server errors.
      const backendError = (err as { response?: { data?: { errors?: unknown } } })
        ?.response?.data?.errors;
      setError(
        typeof backendError === "string" && backendError
          ? backendError
          : "Nie udało się utworzyć zamówienia. Spróbuj ponownie lub skontaktuj się z administratorem."
      );
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div role="dialog" className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg p-6">
        {createdOrders ? (
          <>
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {createdOrders.length > 1
                ? `Utworzono ${createdOrders.length} zamówienia rozliczeniowe`
                : "Utworzono zamówienie rozliczeniowe"}
            </h2>
            <ul className="space-y-1.5 mb-5">
              {createdOrders.map((o) => (
                <li key={o.id} className="text-sm">
                  <Link
                    to={`/orders/${o.id}`}
                    className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                  >
                    {o.order_number}
                  </Link>
                  <span className="text-gray-500 dark:text-gray-400">
                    {" "}— {o.project_name} ({o.cluster_count}{" "}
                    {o.cluster_count === 1 ? "klaster" : "klastrów"})
                  </span>
                </li>
              ))}
            </ul>
            <div className="flex justify-end">
              <Button type="button" onClick={onClose}>Zamknij</Button>
            </div>
          </>
        ) : (
          <>
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Rozlicz klastry dokumentacji
        </h2>

        <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded text-sm">
          <p className="text-gray-700 dark:text-gray-300 font-medium mb-1">
            Wybrane klastry ({selectedClusterIds.length}):
          </p>
          {projectGroups.map((group) => (
            <div key={group.name} className="mb-1.5 last:mb-0">
              {multiProject && (
                <p className="text-xs font-medium text-gray-600 dark:text-gray-300">
                  {group.name}
                </p>
              )}
              <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-0.5">
                {group.clusters.map((c) => (
                  <li key={c.id}>• {c.name} ({c.contractor_name})</li>
                ))}
              </ul>
            </div>
          ))}
          {multiProject && (
            <p className="text-xs text-blue-700 dark:text-blue-300 mt-2">
              Klastry należą do {projectGroups.length} przedsięwzięć — zostanie utworzone
              jedno wspólne zamówienie grupowe (koszt dzielony po równo między przedsięwzięcia).
            </p>
          )}
          {contractorIds.length > 1 && (
            <p className="text-xs text-red-500 mt-1">
              Uwaga: klastry mają różnych podwykonawców. Rozliczenie może się nie powieść.
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <SearchableSelect
            label="Umowa ramowa *"
            placeholder="— wybierz umowę —"
            value={agreementId}
            onChange={(e) => { setAgreementId(e.target.value); setLineItemTypeId(""); }}
            options={agreements.map((a) => ({ value: a.id, label: `${a.title} (${a.contractor_name})` }))}
          />

          <div>
            <SearchableSelect
              label="Pozycja realizacyjna *"
              placeholder="— wybierz pozycję —"
              value={lineItemTypeId}
              onChange={(e) => setLineItemTypeId(e.target.value)}
              disabled={!agreementId}
              options={lineItemTypes.map((lit: LineItemType) => ({ value: lit.id, label: `${lit.name} — ${lit.base_rate} / ${lit.unit}` }))}
            />
            {agreementId && lineItemTypes.length === 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Ta umowa nie ma pozycji realizacyjnych.
              </p>
            )}
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 rounded p-3 text-sm text-blue-800 dark:text-blue-300">
            {selectedClusters.some((c) => c.signing_mode === "paper") ? (
              <>
                Łączna ilość kompletów dokumentacji:{" "}
                <strong>{selectedClusters.reduce((sum, c) => sum + (c.total_quantity ?? 1), 0)}</strong>
                {" "}(suma mnożników z klastrów papierowych + liczba klastrów elektronicznych)
              </>
            ) : (
              <>
                Ilość pozycji w zamówieniu: <strong>{selectedClusterIds.length}</strong> (liczba klastrów)
              </>
            )}
          </div>

          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>Anuluj</Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? "Tworzenie…" : "Utwórz zamówienie"}
            </Button>
          </div>
        </form>
          </>
        )}
      </div>
    </div>
  );
}
